import { inject, injectable } from 'inversify';
import * as THREE from 'three';
import { AutoEventSubscription } from '@/engine/Utilities/AutoEventSubscription';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import { CallbackManager } from '@/engine/Utilities/CallbackManager';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { CubePresentation } from './CubePresentation';
import type { FaceOrientation, ICubePresentationBuilder, ICubeSceneController } from './interfaces';
import { TYPES } from './types';

@injectable()
export class CubeSceneController implements ICubeSceneController {
  private _camera?: THREE.OrthographicCamera;

  private _renderer?: THREE.WebGLRenderer;

  private _sceneEvents?: DisposablesBasket;

  private _cube: CubePresentation;

  private _light: THREE.Light;

  private _scene: THREE.Scene;

  private _isAnatomicalOrientation = true;

  private _isDirectView = true;

  private readonly _orientation: THREE.Quaternion;

  private readonly _sceneFitFactor = 0.7; // 70% of canvas is occupied with cube

  private readonly _orientationChangedListeners: CallbackManager<(quaternion: THREE.Quaternion) => void>;

  private readonly _cubeClikedListeners: CallbackManager<(orientation: FaceOrientation) => void>;

  public constructor(
    @inject(TYPES.ICubePresentationBuilder)
    presentationBuilder: ICubePresentationBuilder
  ) {
    this._orientationChangedListeners = new CallbackManager();
    this._cubeClikedListeners = new CallbackManager();

    this._orientation = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(0, -1, 0)
    );

    this._cube = new CubePresentation(presentationBuilder, this._render.bind(this));
    if (this._cube.getBoundingSphere().center.length() != 0) {
      throw new Error('Cube center point is expected to be [0,0,0]');
    }

    this._light = new THREE.DirectionalLight(0xffffff, 0.5);

    this._scene = new THREE.Scene();

    this._scene.add(this._light);
    this._scene.add(this._cube.getViewModel());
  }

  public dispose(): void {
    this._sceneEvents?.dispose();
    this._renderer?.dispose();
  }

  public onCanvasChanged(canvas: HTMLCanvasElement, bgColor: number): void {
    const sceneSphereFitRadius = (Math.min(canvas.width, canvas.height) * this._sceneFitFactor) / 2; // 80 cube edge length ==> 80*sqrt(3)=139 cube diagonal ==> ~70 bounding sphere radius ==> 200*0.7/2=70

    this._renderer?.dispose();
    this._renderer = CubeSceneController._createRenderer(canvas, bgColor);

    this._sceneEvents?.dispose();
    this._sceneEvents = new DisposablesBasket();

    this._sceneEvents.add(new AutoEventSubscription(canvas, 'pointermove', this._onPointerMove.bind(this)));
    this._sceneEvents.add(new AutoEventSubscription(canvas, 'pointerup', this._onPointerUp.bind(this)));

    this._camera = CubeSceneController._createCamera(
      canvas.width,
      canvas.height,
      sceneSphereFitRadius,
      this._cube.getBoundingSphere().radius,
      this._orientation
    );

    this._synchronizeLightOrientation();

    this._render();
  }

  public setOrientation(quaternion: THREE.Quaternion): void {
    this._orientation.copy(quaternion);

    if (this._camera) {
      const topVector = new THREE.Vector3().setFromMatrixColumn(this._camera.matrix, 1);
      const directionVector = this._camera.getWorldDirection(new THREE.Vector3()).clone();

      const anatomicalAngle = 0.25;
      this._isAnatomicalOrientation =
        directionVector.angleTo(this.getFaceOrientation(directionVector).normal) < anatomicalAngle &&
        topVector.angleTo(this.getFaceOrientation(topVector).normal) < anatomicalAngle;
      this._isDirectView =
        directionVector.angleTo(this.getFaceOrientation(directionVector).normal) < 0.001 &&
        topVector.angleTo(this.getFaceOrientation(topVector).normal) < 0.001;

      this._camera.quaternion.copy(this._orientation);
      this._camera.position.set(0, 0, this._camera.position.length()).applyQuaternion(this._orientation);

      this._synchronizeLightOrientation();

      this._render();
      this._orientationChangedListeners.invoke(quaternion);
    }
  }

  private _synchronizeLightOrientation(): void {
    this._light.quaternion.copy(this._orientation);
    this._light.position.set(0, 0.5, 1).applyQuaternion(this._orientation);
  }

  private _render(): void {
    if (this._camera && this._renderer) {
      this._renderer.render(this._scene, this._camera);
    }
  }

  private static _createRenderer(canvas: HTMLCanvasElement, bgColor: number): THREE.WebGLRenderer {
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      canvas: canvas,
      alpha: true
    });

    renderer.setSize(canvas.width, canvas.height);
    renderer.setClearColor(bgColor, 0);

    return renderer;
  }

  private static _createCamera(
    width: number,
    height: number,
    desiredRoiRadius: number,
    cubeRoiRadius: number,
    orientation: THREE.Quaternion
  ): THREE.OrthographicCamera {
    const camera = new THREE.OrthographicCamera(
      -width / 2,
      width / 2,
      height / 2,
      -height / 2,
      0.1,
      (cubeRoiRadius + 1) * 2
    );

    camera.quaternion.copy(orientation);
    camera.position.set(0, 0, cubeRoiRadius + 1).applyQuaternion(camera.quaternion);
    camera.zoom = desiredRoiRadius / cubeRoiRadius;
    camera.updateProjectionMatrix();

    return camera;
  }

  public getFaceOrientation(direction: THREE.Vector3): FaceOrientation {
    const raycaster = new THREE.Raycaster(this._cube.getBoundingSphere().center.clone(), direction);
    const intersections = raycaster.intersectObject(this._cube.getFaces());

    const normal = direction.clone();
    const top = new THREE.Vector3();

    if (intersections.length !== 0) {
      const obj = intersections[0].object;
      const cs = obj.matrixWorld;
      normal.copy(new THREE.Vector3(0, 0, 1).transformDirection(cs));
      top.copy(new THREE.Vector3(0, 1, 0).transformDirection(cs));
    }

    return { normal: normal, top: top };
  }

  public trackCameraOrientationChanges(callback: (quaternion: THREE.Quaternion) => void): CallbackSubscription {
    return this._orientationChangedListeners.subscribe(callback);
  }

  public trackCubeClicked(callback: (orientation: FaceOrientation) => void): CallbackSubscription {
    return this._cubeClikedListeners.subscribe(callback);
  }

  public getCamera(): THREE.Camera {
    return this._camera as THREE.Camera;
  }

  private _highlightFace(mouse: THREE.Vector2): void {
    this._cube.applyDefaultFaceColor();

    if (this._camera) {
      const intersection = this._getHighlightedFace(this._camera, mouse);
      if (intersection) {
        // apply transparency if intersected face isn't on front
        if (intersection.instanceId !== 0) {
          this._cube.applyTransparency();
        }
        this._cube.highlightFace(intersection.object);
      }
    }
  }

  private _onPointerMove(event: Event): void {
    const mouse = this._getMousePosition(event);

    this._highlightFace(mouse);

    this._render();
  }

  private _onPointerUp(event: Event): void {
    const mouse = this._getMousePosition(event);

    if (this._camera) {
      const intersection = this._getHighlightedFace(this._camera, mouse);
      if (intersection?.face) {
        const cs = intersection.object.matrixWorld;
        const normal = new THREE.Vector3(0, 0, 1).transformDirection(cs);
        const top = new THREE.Vector3(0, 1, 0).transformDirection(cs);
        this._cubeClikedListeners.invoke({ normal: normal, top: top });
        this._highlightFace(mouse);
      }
    }

    this._render();
  }

  private _getHighlightedFace(camera: THREE.Camera, mouse: THREE.Vector2): THREE.Intersection | null {
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, camera);
    const intersections = raycaster.intersectObject(this._cube.getFaces());

    if (intersections.length !== 0) {
      // in anatomical orientation highlight back face
      if (this._isDirectView) {
        const intersection = intersections.reduce((prev, curr) => {
          return prev.distance > curr.distance ? prev : curr;
        });

        intersection.instanceId = 1;
        return intersection;
      } else {
        // find face closest to the center of bounding box
        let faceIndex = 0;
        let distanceToCenter = Number.MAX_VALUE;

        for (let i = 0; i < intersections.length; i++) {
          const mesh = intersections[i].object;
          const bbox = new THREE.Box3().setFromObject(mesh);

          const center = new THREE.Vector3();
          bbox?.getCenter(center);

          const centerDistance = center.distanceTo(intersections[i].point);
          if (centerDistance < distanceToCenter) {
            distanceToCenter = centerDistance;
            faceIndex = i;
          }
        }

        // check if face in hovered by another face
        // if it's back side then cube should be transparent
        const distanceToFace = raycaster.ray.origin.distanceTo(intersections[faceIndex].point);
        let isBackFace = false;

        for (let i = 0; i < intersections.length; i++) {
          if (i === faceIndex) {
            continue;
          }

          const faceDistance = raycaster.ray.origin.distanceTo(intersections[i].point);
          if (faceDistance < distanceToFace) {
            isBackFace = true;
            break;
          }
        }

        const intersection = intersections[faceIndex];
        intersection.instanceId = 0;

        // intersection is covered by another face
        if (isBackFace) {
          intersection.instanceId = 1;
        }

        return intersection;
      }
    }

    return null;
  }

  private _getMousePosition(event: Event): THREE.Vector2 {
    const pointEvent = event as PointerEvent;
    event.stopPropagation();
    event.preventDefault();

    const targetOffset = (event.target as HTMLElement).getBoundingClientRect();
    const offsetX = pointEvent.clientX - targetOffset.left;
    const offsetY = pointEvent.clientY - targetOffset.top;

    const mouse = new THREE.Vector2();

    const size = new THREE.Vector2();
    this._renderer?.getSize(size);

    mouse.x = (offsetX / size.x) * 2 - 1;
    mouse.y = (-offsetY / size.y) * 2 + 1;

    return mouse;
  }

  public isAnatomicalOrientation(): boolean {
    return this._isAnatomicalOrientation;
  }
}
